import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Stack,
} from "@mui/material";
import Typography from "common/components/Typography";
import PageHeader from "common/components/PageHeader";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedState } from "common/hooks/useDebounce";
import { useTranslation } from "react-i18next";
import { Icon4Star, Icon5Star } from "assets/icons";
import { useErrorNotification } from "common/components/Notification";
import { RoomContext } from "App/context";
import { WithGrow } from "common/components/WithGrow";
import Paper from "common/components/Paper";
import PlayerCard from "./PlayerCard";
import { getPlayers } from "service/player";
import { CharacterBox, DataList, Player } from "service/player/type";
import ComparePlayerCard from "./ComparePlayerCard";
import RowCharacter from "./RowCharacter";
import { DraftParams } from "service/draft/type";
import { getDraftParams, getScore } from "service/draft";
import BanButton from "./BanButton";
import { Backspace, ExpandMore } from "@mui/icons-material";
import { PlayersNumber, PlayersNumberDictionary } from "types/Room/type";
import { applyBoxPreferences } from "common/utils";
import { Character } from "types/Character/type";
import { usePlayerScore } from "common/hooks/usePlayerScore";

type PlayersBoxProps = {};

const PlayersBox = (props: PlayersBoxProps) => {
  const { localPlayer, charactersList, availableSheets } =
    useContext(RoomContext);
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const limit = 20;
  const [debouncedValue, setDebouncedValue] = useDebouncedState<string>(
    search,
    (value) => {
      setSearch(value);
      setPage(1);
    },
  );
  const [playersList, setPlayersList] = useState<Player[]>([]);
  const [playersMeta, setPlayersMeta] = useState<DataList<Player>["meta"]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [player1, setPlayer1] = useState<Player | undefined>(localPlayer);
  const [scoreP1, setScoreP1] = useState<number | undefined>();
  const [player2, setPlayer2] = useState<Player | undefined>();
  const [scoreP2, setScoreP2] = useState<number | undefined>();
  const [player3, setPlayer3] = useState<Player | undefined>();
  const [scoreP3, setScoreP3] = useState<number | undefined>();
  const [player4, setPlayer4] = useState<Player | undefined>();
  const [scoreP4, setScoreP4] = useState<number | undefined>();
  const [scoreP1AndP3, setScoreP1AndP3] = useState<number | undefined>();
  const [scoreP2AndP4, setScoreP2AndP4] = useState<number | undefined>();
  const [draftParams, setDraftParams] = useState<DraftParams | undefined>();
  const [selectedSheet, setSelectedSheet] = useState<number | undefined>(
    availableSheets[0]?.id,
  );
  const [selectedPlayersNumber, setSelectedPlayersNumber] =
    useState<PlayersNumber>(PlayersNumber._1vs1);
  const is1V1 = selectedPlayersNumber === PlayersNumber._1vs1;
  const is2V2 = selectedPlayersNumber === PlayersNumber._2vs2;
  const sheet = availableSheets.find((s) => s.id === selectedSheet);

  const mergeBoxes = (box1: CharacterBox[], box2: CharacterBox[]) => {
    const boxByName = new Map<string, number>();
    box1.forEach(({ name, constellation }) => {
      boxByName.set(name, constellation);
    });
    box2.forEach(({ name, constellation }) => {
      const existing = boxByName.get(name);
      boxByName.set(name, Math.max(existing ?? -1, constellation));
    });
    return Array.from(boxByName.entries()).map(([name, constellation]) => ({
      name,
      constellation,
    }));
  };

  const isCharacterHidden = (
    player: Player | undefined,
    character: Character,
  ) =>
    player?.preferences
      ?.find((p) => p.sheetId === selectedSheet)
      ?.characters.some((c) => c.name === character.name) ?? false;

  const getConstellation = (
    player: Player | undefined,
    character: Character,
  ) =>
    isCharacterHidden(player, character)
      ? undefined
      : player?.characters?.find((c) => c.name === character.name)
          ?.constellation;

  const getLevel = (player: Player | undefined, character: Character) =>
    isCharacterHidden(player, character)
      ? undefined
      : player?.characters?.find((c) => c.name === character.name)?.lvl;

  useEffect(() => {
    const fetchPlayers = async () => {
      setIsLoading(true);
      try {
        const data = await getPlayers(page, limit, debouncedValue);
        setPlayersList(data.data);
        setPlayersMeta(data.meta);
      } catch (e) {
        dispatchError(e as string);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlayers();
  }, [page, limit, debouncedValue]);

  usePlayerScore(player1, selectedSheet, setScoreP1);
  usePlayerScore(player2, selectedSheet, setScoreP2);
  usePlayerScore(player3, selectedSheet, setScoreP3);
  usePlayerScore(player4, selectedSheet, setScoreP4);

  useEffect(() => {
    if (selectedSheet === undefined && availableSheets.length > 0) {
      setSelectedSheet(availableSheets[0].id);
    }
  }, [availableSheets, selectedSheet]);

  const selectedPlayers = [player1, player2, player3, player4];
  const selectedSetters = [
    setPlayer1,
    setPlayer2,
    setPlayer3,
    setPlayer4,
  ] as const;

  const togglePlayerSelection = (player: Player) => {
    const slotIndex = selectedPlayers.findIndex(
      (selectedPlayer) => selectedPlayer?.id === player.id,
    );

    if (slotIndex !== -1) {
      selectedSetters[slotIndex](undefined);
      return;
    }

    if (!player1) {
      setPlayer1(player);
      return;
    }

    if (is2V2 && !player3) {
      setPlayer3(player);
      return;
    }

    if (!player2 || is1V1) {
      setPlayer2(player);
      return;
    }

    if (is2V2 && !player4) {
      setPlayer4(player);
    }
  };

  const getPreferences = (player: Player | undefined) =>
    player?.preferences?.find((p) => p.sheetId === selectedSheet)?.characters;

  const computeDraftParams = async () => {
    if (selectedSheet === undefined) return undefined;

    const apply = (player: Player) =>
      applyBoxPreferences(player.characters ?? [], getPreferences(player));

    if (is1V1 && player1 && player2) {
      return await getDraftParams(
        apply(player1),
        apply(player2),
        selectedSheet,
      );
    }

    if (is2V2 && player1 && player2 && player3 && player4) {
      const p1Andp3 = mergeBoxes(apply(player1), apply(player3));
      const p2Andp4 = mergeBoxes(apply(player2), apply(player4));
      setScoreP1AndP3(await getScore(p1Andp3, selectedSheet));
      setScoreP2AndP4(await getScore(p2Andp4, selectedSheet));
      return await getDraftParams(p1Andp3, p2Andp4, selectedSheet);
    }

    return undefined;
  };

  useEffect(() => {
    const compute = async () => {
      setDraftParams(await computeDraftParams());
    };

    compute();
  }, [selectedSheet, is1V1, is2V2, player1, player2, player3, player4]);

  return (
    <WithGrow
      direction="column"
      sx={{
        gap: 2,
      }}
      fixedSx={{
        width: "100%",
        alignItems: "center",
      }}
      fixed={
        <React.Fragment>
          <PageHeader label={t("Box des joueurs")} />
          <Stack
            width="100%"
            direction="row"
            justifyContent="start"
            gap={1}
            pt={1}
          >
            <FormControl size="small">
              <InputLabel>{t("Chercher")}</InputLabel>
              <OutlinedInput
                value={debouncedValue}
                onChange={(event) => setDebouncedValue(event.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setDebouncedValue("")}>
                      <Backspace />
                    </IconButton>
                  </InputAdornment>
                }
                label={t("Chercher")}
              />
            </FormControl>
            <FormControl sx={{ width: "15rem" }}>
              <InputLabel>{t("Feuille d'équilibrage")}</InputLabel>
              <Select
                label={t("Feuille d'équilibrage")}
                value={selectedSheet ?? ""}
                onChange={(evt) => {
                  setSelectedSheet(evt.target.value as number);
                  setScoreP1(undefined);
                  setScoreP2(undefined);
                  setScoreP1AndP3(undefined);
                  setScoreP2AndP4(undefined);
                  setDraftParams(undefined);
                }}
              >
                {availableSheets.map((s) => (
                  <MenuItem value={s.id} key={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: "10rem" }}>
              <InputLabel>{t("Nombre de joueurs")}</InputLabel>
              <Select
                label={t("Nombre de joueurs")}
                value={selectedPlayersNumber}
                onChange={(evt) => {
                  setSelectedPlayersNumber(evt.target.value as PlayersNumber);
                  setPlayer3(undefined);
                  setPlayer4(undefined);
                }}
              >
                {Object.values(PlayersNumber).map((n, i) => (
                  <MenuItem value={n} key={i}>
                    {PlayersNumberDictionary[n]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </React.Fragment>
      }
    >
      <Stack direction="row" height="100%" gap={2}>
        <Stack height={0} minHeight="100%" flex={1}>
          <Paper
            sx={{
              backgroundColor: "unset",
              width: "100%",
              display: "grid",
              gridTemplateColumns:
                playersList.length > 0
                  ? "repeat(auto-fill, minmax(130px, 1fr))"
                  : "",
              gap: (theme) => theme.spacing(2),
              overflow: "auto",
              height: "100%",
            }}
          >
            {isLoading ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <CircularProgress />
              </Stack>
            ) : playersList.length === 0 ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ height: "100%" }}
              >
                <Typography>{t("Aucun joueur trouvé")}</Typography>
              </Stack>
            ) : (
              playersList
                .sort((playerA, playerB) =>
                  (playerA.pseudo ?? "").localeCompare(playerB.pseudo ?? ""),
                )
                .map((player, index) => (
                  <PlayerCard
                    key={index}
                    player={player}
                    selected={
                      player1?.id === player.id ||
                      player2?.id === player.id ||
                      player3?.id === player.id ||
                      player4?.id === player.id
                    }
                    onClick={() => togglePlayerSelection(player)}
                  />
                ))
            )}
          </Paper>
          {playersMeta?.pages && playersMeta.pages > 1 && (
            <Stack alignItems="center" pt={1}>
              <Pagination
                count={Math.max(playersMeta.pages, 1)}
                page={page}
                onChange={(_, value) => setPage(value)}
                shape="rounded"
                color="primary"
              />
            </Stack>
          )}
        </Stack>
        <Stack flex={1} height={0} minHeight="100%">
          <Paper sx={{ height: "100%" }}>
            <Stack direction="column" height="100%" gap={2}>
              <Stack direction="row" justifyContent="space-evenly" gap={1}>
                <Stack direction="row" justifyContent="center" gap={1}>
                  <ComparePlayerCard
                    player={player1}
                    onDelete={() => setPlayer1(undefined)}
                    color="player1"
                    sheet={sheet}
                    score={scoreP1}
                  />
                  {is2V2 && (
                    <ComparePlayerCard
                      player={player3}
                      onDelete={() => setPlayer3(undefined)}
                      color="player1"
                      sheet={sheet}
                      score={scoreP3}
                    />
                  )}
                  <ComparePlayerCard
                    player={player2}
                    onDelete={() => setPlayer2(undefined)}
                    color="player2"
                    sheet={sheet}
                    score={scoreP2}
                  />
                  {is2V2 && (
                    <ComparePlayerCard
                      player={player4}
                      onDelete={() => setPlayer4(undefined)}
                      color="player2"
                      sheet={sheet}
                      score={scoreP4}
                    />
                  )}
                </Stack>
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                >
                  {is2V2 && (
                    <Stack direction="row" gap={2}>
                      <Typography
                        sx={{
                          color: ({ palette }) => palette.common.player1.text,
                        }}
                      >
                        {scoreP1AndP3 ?? "..."}
                      </Typography>
                      <Typography
                        sx={{
                          color: ({ palette }) => palette.common.player2.text,
                        }}
                      >
                        {scoreP2AndP4 ?? "..."}
                      </Typography>
                    </Stack>
                  )}
                  <BanButton
                    badgeQuantity={draftParams?.jokerBans}
                    whoBan={
                      (draftParams?.jokerBans ?? 0) < 0 ? "player1" : "player2"
                    }
                    label={t("Bans joker")}
                  />
                  <BanButton
                    badgeQuantity={draftParams?.balancingBans}
                    whoBan={
                      (draftParams?.balancingBans ?? 0) < 0
                        ? "player1"
                        : "player2"
                    }
                    label={t("Bans d'équilibrage")}
                  />
                </Stack>
              </Stack>
              <Stack overflow="auto">
                {player1 && player2 && (is2V2 ? player3 && player4 : true) && (
                  <>
                    <Accordion
                      sx={{
                        bgcolor: ({ palette }) => palette.background.highlight,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                          display="flex"
                          sx={{ svg: { width: "3.5rem", height: "3.5rem" } }}
                        >
                          <Icon5Star />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack>
                          <Divider orientation="horizontal" />
                          {charactersList
                            .filter((c) => c.rarity === 5)
                            .map((character, index) => (
                              <React.Fragment key={index}>
                                <RowCharacter
                                  character={character}
                                  is2V2={is2V2}
                                  constellationPlayer1={getConstellation(
                                    player1,
                                    character,
                                  )}
                                  levelPlayer1={getLevel(player1, character)}
                                  constellationPlayer2={getConstellation(
                                    player2,
                                    character,
                                  )}
                                  levelPlayer2={getLevel(player2, character)}
                                  constellationPlayer3={getConstellation(
                                    player3,
                                    character,
                                  )}
                                  constellationPlayer4={getConstellation(
                                    player4,
                                    character,
                                  )}
                                />
                                <Divider orientation="horizontal" />
                              </React.Fragment>
                            ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                    <Accordion
                      sx={{
                        bgcolor: ({ palette }) => palette.background.highlight,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box
                          display="flex"
                          sx={{ svg: { width: "3.5rem", height: "3.5rem" } }}
                        >
                          <Icon4Star />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack>
                          <Divider orientation="horizontal" />
                          {charactersList
                            .filter((c) => c.rarity === 4)
                            .map((character, index) => (
                              <React.Fragment key={index}>
                                <RowCharacter
                                  character={character}
                                  is2V2={is2V2}
                                  constellationPlayer1={getConstellation(
                                    player1,
                                    character,
                                  )}
                                  levelPlayer1={getLevel(player1, character)}
                                  constellationPlayer2={getConstellation(
                                    player2,
                                    character,
                                  )}
                                  levelPlayer2={getLevel(player2, character)}
                                  constellationPlayer3={getConstellation(
                                    player3,
                                    character,
                                  )}
                                  constellationPlayer4={getConstellation(
                                    player4,
                                    character,
                                  )}
                                />
                                <Divider orientation="horizontal" />
                              </React.Fragment>
                            ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </WithGrow>
  );
};

export default PlayersBox;
